import React, { useRef, useState } from 'react';
import { X, Layers, Upload, Image as ImageIcon, Plus, XCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { createTextPicker, getLocalizedField } from '../../utils/localizedContent';


// Definido FUERA del componente para que React no lo recree en cada render
// (evita que los inputs pierdan foco tras cada tecla)
function Field({ label, required, children, className = '' }) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label className="text-[#A1A1AA] text-xs font-medium">
        {label}{required && <span className="text-[#EF4444] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function ProductForm({ form, setForm, editing, productTypes, boosters, imagePreview, setImagePreview, onSubmit, onClose, lang, setMessage, fieldErrors = {} }) {
  const fileInputRef = useRef(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const l = createTextPicker(lang);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingImages(true);
    const newPreviews = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      newPreviews.push({ id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, preview: base64, isLocal: true });
    }
    setImagePreview(prev => [...prev, ...newPreviews]);
    setUploadingImages(false);
  };

  const handleAddImageUrl = () => {
    if (!form.imageUrl?.trim()) return;
    setImagePreview(prev => [...prev, { id: `url_${Date.now()}`, preview: form.imageUrl.trim(), url: form.imageUrl.trim(), isLocal: false }]);
    setForm(f => ({ ...f, imageUrl: '' }));
  };

  const handleRemoveImage = (imageId) => {
    setImagePreview(prev => prev.filter(img => img.id !== imageId));
  };

  const inputClass = "w-full bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#10B981] transition-colors";

  const inputCls = (field) =>
    `w-full bg-[#0A0A0E] border rounded-xl px-4 py-2.5 text-white text-sm outline-none transition-colors ${
      fieldErrors[field] ? 'border-[#EF4444] focus:border-[#EF4444]' : 'border-white/10 focus:border-[#10B981]'
    }`;

  const FieldError = ({ field }) =>
    fieldErrors[field] ? <p className="text-[#EF4444] text-xs mt-0.5">{fieldErrors[field]}</p> : null;

  const isBoardGame = form.category === 'board_game';

  return (
    <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6 mb-6">
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-white font-bold text-base">
          {editing ? l('Editar Producto', 'Edit Product') : l('Nuevo Producto', 'New Product')}
        </h3>
        <button onClick={onClose} className="text-[#A1A1AA] hover:text-white transition-colors">
          <X size={18} />
        </button>
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Name EN */}
        <Field label={l('Nombre (EN)', 'Name (EN)')} required>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
            placeholder="e.g. Catan" className={inputCls('name')} data-testid="product-name-input" />
          <FieldError field="name" />
        </Field>

        {/* Name ES */}
        <Field label={l('Nombre (ES)', 'Name (ES)')}>
          <input value={form.name_es} onChange={e => setForm(f => ({ ...f, name_es: e.target.value }))}
            placeholder="ej. Colonos de Catán" className={inputCls('name_es')} />
        </Field>

        {/* Category */}
        <Field label={l('Categoría', 'Category')} required>
          <select value={form.category} onChange={e => {
            const nextCategory = e.target.value;
            setForm(f => ({
              ...f,
              category: nextCategory,
              is_rentable: nextCategory === 'board_game' ? f.is_rentable : false,
              available_for_rent: nextCategory === 'board_game' ? f.available_for_rent : 0,
              min_players: nextCategory === 'board_game' ? f.min_players : '',
              max_players: nextCategory === 'board_game' ? f.max_players : '',
              duration_minutes: nextCategory === 'board_game' ? f.duration_minutes : '',
            }));
          }}
            className={inputCls('category')} data-testid="product-category-select">
            {productTypes.length === 0 && (
              <option value="board_game">{l('Juego de Mesa', 'Board Game')}</option>
            )}
            {productTypes.map(type => (
              <option key={type.type_id} value={type.type_id}>
                {getLocalizedField(type, 'name', lang, type.name)}
              </option>
            ))}
          </select>
        </Field>

        {/* Price */}
        <Field label={l('Precio (€)', 'Price (€)')} required>
          <input value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
            required type="number" step="0.01" min="0" placeholder="0.00"
            className={inputCls('price')} data-testid="product-price-input" />
          <FieldError field="price" />
        </Field>

        {/* Tags */}
        <Field label={l('Etiquetas (separadas por coma)', 'Tags (comma separated)')}>
          <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
            placeholder={l('estrategia, familia, 2 jugadores', 'strategy, family, 2 players')}
            className={inputClass} />
        </Field>

        {isBoardGame && (
          <>
            <Field label={l('Mínimo de jugadores', 'Minimum players')}>
              <input
                value={form.min_players}
                onChange={e => setForm(f => ({ ...f, min_players: e.target.value }))}
                type="number"
                min="1"
                placeholder="1"
                className={inputClass}
              />
            </Field>
            <Field label={l('Máximo de jugadores', 'Maximum players')}>
              <input
                value={form.max_players}
                onChange={e => setForm(f => ({ ...f, max_players: e.target.value }))}
                type="number"
                min="1"
                placeholder="4"
                className={inputClass}
              />
            </Field>
            <Field label={l('Duración (minutos)', 'Duration (minutes)')}>
              <input
                value={form.duration_minutes}
                onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                type="number"
                min="1"
                placeholder="60"
                className={inputClass}
              />
            </Field>
          </>
        )}

        {/* Description EN */}
        <Field label={l('Descripción (EN)', 'Description (EN)')} required className="sm:col-span-2">
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            required placeholder={l('Descripción en inglés...', 'English description...')} rows={2}
            className={`${inputClass} resize-none`} />
        </Field>

        {/* Description ES */}
        <Field label={l('Descripción (ES)', 'Description (ES)')} className="sm:col-span-2">
          <textarea value={form.description_es} onChange={e => setForm(f => ({ ...f, description_es: e.target.value }))}
            placeholder={l('Descripción en español...', 'Spanish description...')} rows={2}
            className={`${inputClass} resize-none`} />
        </Field>

        {/* Images */}
        <div className="sm:col-span-2 space-y-3">
          <label className="text-[#A1A1AA] text-xs font-medium flex items-center gap-1.5">
            <ImageIcon size={12} />{l('Imágenes del Producto', 'Product Images')}
          </label>
          {imagePreview.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {imagePreview.map(img => (
                <div key={img.id} className="relative group">
                  <img src={img.preview} alt="Preview" className="w-20 h-20 object-cover rounded-lg border border-white/10" />
                  <button type="button" onClick={() => handleRemoveImage(img.id)}
                    className="absolute -top-2 -right-2 bg-[#EF4444] rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <XCircle size={16} className="text-white" />
                  </button>
                  {img.isLocal && <span className="absolute bottom-1 left-1 text-[8px] bg-[#10B981] text-white px-1 rounded">NEW</span>}
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingImages}
              className="flex-1 flex items-center justify-center gap-2 bg-[#0A0A0E] border border-dashed border-white/20 rounded-xl px-4 py-3 text-[#A1A1AA] hover:border-[#10B981] hover:text-[#10B981] transition-colors"
              data-testid="upload-image-btn">
              <Upload size={16} />
              {uploadingImages ? l('Procesando...', 'Processing...') : l('Subir Imágenes', 'Upload Images')}
            </button>
            <div className="flex-1 flex gap-2">
              <input value={form.imageUrl || ''} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                placeholder={l('URL de imagen...', 'Image URL...')}
                className="flex-1 bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#10B981]"
                data-testid="image-url-input" />
              <button type="button" onClick={handleAddImageUrl}
                className="px-4 py-2 bg-[#1E1E2A] border border-white/10 rounded-xl text-[#A1A1AA] hover:text-white hover:border-[#10B981] transition-colors"
                data-testid="add-url-btn">
                <Plus size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Booster linking */}
        {form.category === 'tcg' && boosters.length > 0 && (
          <div className="sm:col-span-2">
            <Field label={l('Vincular a Booster/Set (opcional)', 'Link to Booster/Set (optional)')}>
              <select value={form.booster_id || ''} onChange={e => setForm(f => ({ ...f, booster_id: e.target.value }))}
                className={inputClass} data-testid="product-booster-select">
                <option value="">{l('-- Sin vincular --', '-- Not linked --')}</option>
                {boosters.map(b => (
                  <option key={b.booster_id} value={b.booster_id}>
                    [{b.tcg_type?.toUpperCase()}] {b.name} ({b.set_code})
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {/* Rentable (board games only) */}
        {isBoardGame && (
          <>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!form.is_rentable}
                onChange={e => setForm(f => ({ ...f, is_rentable: e.target.checked }))}
                className="w-4 h-4 accent-[#10B981]" />
              <span className="text-[#A1A1AA] text-sm">{l('Disponible para alquiler', 'Available to rent')}</span>
            </label>
            {form.is_rentable && (
              <Field label={l('Copias para alquiler', 'Copies for rent')}>
                <input value={form.available_for_rent} onChange={e => setForm(f => ({ ...f, available_for_rent: e.target.value }))}
                  type="number" min="0" placeholder="1" className={inputClass} />
              </Field>
            )}
          </>
        )}

        <button type="submit" className="sm:col-span-2 btn-primary py-2.5 text-sm" data-testid="submit-product-btn">
          {editing ? l('Guardar Cambios', 'Save Changes') : l('Crear Producto', 'Create Product')}
        </button>
      </form>
    </div>
  );
}
